import { z } from 'zod';
import { TenantSummaryUsageTypeSchema } from './tenant-summary';
import { CreateTenantSchema, RentSchema, RentalStatusSchema, RentalAgreementTypeSchema, RentPaymentPeriodSchema, RentPaymentScopeSchema, } from './valuation-tenants';
const rent = z.object({
    amount: z.number().finite().nonnegative(),
    payment_period: RentPaymentPeriodSchema,
    payment_scope: RentPaymentScopeSchema,
    source_cell: z.string(),
});
const tenant = z.object({
    source_ids: z.array(z.string()).min(1),
    source_label: z.string(),
    structure_uuid: z.string().uuid().nullable(),
    reference_number: z.string().nullable(),
    tenant_name: z.string().nullable(),
    usage_type: TenantSummaryUsageTypeSchema.nullable(),
    sub_usage_type: z.string(),
    rental_status: RentalStatusSchema.nullable(),
    floor: z.string().nullable(),
    area_sqm: z.number().finite().nonnegative().nullable(),
    number_of_units: z.number().int().nonnegative().nullable(),
    lease_type: RentalAgreementTypeSchema.nullable(),
    private_person: z.boolean(),
    lease_number: z.string().nullable(),
    start_date: z.string().nullable(),
    end_date: z.string().nullable(),
    contract_rent: rent.nullable(),
    market_rent: rent.nullable().optional(),
    options: z.object({ frequency: z.number().int().positive(), years: z.number().int().positive() }).nullable(),
    graduations: z.array(z.object({ date: z.string(), rent })),
    rent_free_periods: z.array(z.object({ start_date: z.string(), end_date: z.string() })),
    vacancy_periods: z.array(z.object({
        start_date: z.string().nullable(),
        end_date: z.string().nullable(),
        duration: z.number().positive().multipleOf(0.5).nullable(),
    })),
    note: z.string().nullable(),
    blocking_reason: z.string().nullable(),
    validation_issues: z.array(z.object({ field: z.string().nullable(), message: z.string() })),
    prepared: z
        .object({
        tenant: CreateTenantSchema,
        tenant_parameters: z.object({ reasonable_rent: RentSchema }).nullish(),
    })
        .nullable(),
});
const clarificationQuestion = z.object({
    id: z.string(),
    source_ids: z.array(z.string()).min(1),
    field: z.enum([
        'structure_uuid',
        'usage_type',
        'rental_status',
        'number_of_units',
        'lease_type',
        'contract_rent',
        'market_rent',
        'area_sqm',
        'start_date',
        'end_date',
        'interpretation',
    ]),
    input_type: z.enum(['single_choice', 'text', 'confirmation']),
    title: z.string(),
    reason: z.string(),
    recommended_option_id: z.string(),
    options: z.array(z.object({
        id: z.string(),
        label: z.string(),
        instruction: z.string(),
        consequence: z.string(),
    })),
});
const tenantWorkbookCellSchema = z.object({
    column: z.string().regex(/^[A-Z]{1,3}$/),
    value: z.union([z.string(), z.number().finite(), z.boolean(), z.null()]),
    formatted: z.string().nullish(),
    formula: z.string().nullish(),
    comment: z.string().nullish(),
    hidden: z.boolean().optional(),
});
export const tenantWorkbookSchema = z.object({
    version: z.literal(1),
    filename: z.string(),
    sheets: z
        .array(z.object({
        name: z.string(),
        hidden: z.boolean(),
        merges: z.array(z.string()),
        rows: z.array(z.object({ number: z.number().int().positive(), cells: z.array(tenantWorkbookCellSchema) })),
    }))
        .min(1),
});
export const tenantImportResultSchema = z.object({
    failure_reason: z.string().nullish(),
    rows: z.array(tenant),
    questions: z.array(clarificationQuestion),
    source_fingerprint: z.string(),
    ready: z.boolean(),
});
